/**
 * useShortlistCount.ts
 *
 * File-level:
 * - Hook to fetch the number of unique profileIds the current user has shortlisted.
 * - Robust: tries multiple backend endpoints and tolerates multiple response shapes.
 *
 * Notes:
 * - fetchJSON used across the repo prefixes requests with `/api`, DO NOT add an extra `/api` here.
 */

import { useEffect, useState } from 'react';
import { fetchJSON } from '@/utils/api';

/**
 * ShortlistCountResult
 *
 * @interface
 * @description Return shape from useShortlistCount
 */
export interface ShortlistCountResult {
  /** number of unique shortlisted profileIds or null when unknown */
  count: number | null;
  /** loading state while fetching */
  loading: boolean;
  /** any error encountered while fetching */
  error: Error | null;
}

/**
 * normalizeToUniqueCount
 *
 * @description Given a backend response (any shape), attempt to extract a unique
 * list of profileIds and return the deduplicated length. Handles:
 * - { profileIds: [...] }
 * - { shortlistedProfiles: [...] } where each item may have profileId or userId
 * - { profiles: [...] }
 * - { rows: [...] }
 * - { total: number } / { count: number }
 *
 * @param json any
 * @returns number | null
 */
function normalizeToUniqueCount(json: any): number | null {
  try {
    if (!json) return null;

    // Direct arrays
    if (Array.isArray(json.profileIds)) {
      return Array.from(new Set(json.profileIds.map(String))).length;
    }
    if (Array.isArray(json.shortlistedProfiles)) {
      const ids = json.shortlistedProfiles
        .map((it: any) => it.profileId ?? it.userId ?? it.id ?? '')
        .filter(Boolean);
      return Array.from(new Set(ids.map(String))).length;
    }
    if (Array.isArray(json.profiles)) {
      const ids = json.profiles
        .map((it: any) => it.profileId ?? it.userId ?? it.id ?? '')
        .filter(Boolean);
      if (ids.length) return Array.from(new Set(ids.map(String))).length;
      return json.profiles.length;
    }
    if (Array.isArray(json.rows)) {
      const ids = json.rows
        .map((it: any) => it.profileId ?? it.userId ?? it.id ?? '')
        .filter(Boolean);
      return Array.from(new Set(ids.map(String))).length;
    }

    // Some endpoints return `profiles` or `shortlistedProfiles` nested under `data`
    if (json.data) {
      const inner = json.data;
      if (Array.isArray(inner.profileIds)) {
        return Array.from(new Set(inner.profileIds.map(String))).length;
      }
      if (Array.isArray(inner.shortlistedProfiles)) {
        const ids = inner.shortlistedProfiles
          .map((it: any) => it.profileId ?? it.userId ?? it.id ?? '')
          .filter(Boolean);
        return Array.from(new Set(ids.map(String))).length;
      }
      if (Array.isArray(inner.profiles)) {
        const ids = inner.profiles
          .map((it: any) => it.profileId ?? it.userId ?? it.id ?? '')
          .filter(Boolean);
        return Array.from(new Set(ids.map(String))).length;
      }
    }

    // Numeric totals
    if (typeof json.total === 'number') return Number(json.total);
    if (typeof json.count === 'number') return Number(json.count);
    if (typeof json.length === 'number') return Number(json.length);

    // Try to find the first array value anywhere and count unique ids
    const arr = Object.values(json).find((v) => Array.isArray(v)) as any;
    if (Array.isArray(arr)) {
      const ids = arr
        .map((it: any) => (typeof it === 'string' ? it : (it.profileId ?? it.userId ?? it.id ?? null)))
        .filter(Boolean);
      if (ids.length) {
        return Array.from(new Set(ids.map(String))).length;
      }
      return arr.length;
    }

    return null;
  } catch (e) {
    // swallow and return null
    // eslint-disable-next-line no-console
    console.debug('normalizeToUniqueCount error', e);
    return null;
  }
}

/**
 * useShortlistCount
 *
 * @description
 * - Fetches the shortlisted profiles for a given userId and returns the unique profile count.
 * - Tries multiple endpoints for compatibility with varying backend shapes.
 *
 * @param userId string | null - Logged-in user's id. If null, hook returns { count: null, loading: false }.
 * @param pollInterval optional number in ms to poll the endpoint (default: none)
 * @returns ShortlistCountResult
 */
export default function useShortlistCount(userId: string | null, pollInterval?: number): ShortlistCountResult {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    let intervalId: number | undefined;

    async function load() {
      if (!userId) {
        setCount(null);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      /**
       * IMPORTANT:
       * fetchJSON already prefixes requests with "/api".
       * Do NOT add an additional "/api" prefix here or you'll get /api/api/...
       *
       * Endpoints (relative to /api) to try, in order of preference.
       */
      const endpoints = [
        `/simple-shortlist/${encodeURIComponent(userId)}`,
        `/shortlist/list-normalized/${encodeURIComponent(userId)}`,
        `/shortlist/list/${encodeURIComponent(userId)}`,
        `/shortlist/list-normalized/${encodeURIComponent(userId)}`, // fallback duplicate
      ];

      let resolved: number | null = null;
      let lastErr: any = null;

      for (const ep of endpoints) {
        if (cancelled) return;
        try {
          // eslint-disable-next-line no-console
          console.debug('[useShortlistCount] trying endpoint:', ep);
          const json = await fetchJSON(ep);
          // eslint-disable-next-line no-console
          console.debug('[useShortlistCount] response from', ep, json);

          const extracted = normalizeToUniqueCount(json);
          if (typeof extracted === 'number') {
            resolved = extracted;
            // eslint-disable-next-line no-console
            console.debug('[useShortlistCount] resolved count from', ep, resolved);
            break;
          }

          // Some endpoints respond with success:false but provide rows in data
          if (json && json.data) {
            const extracted2 = normalizeToUniqueCount(json.data);
            if (typeof extracted2 === 'number') {
              resolved = extracted2;
              // eslint-disable-next-line no-console
              console.debug('[useShortlistCount] resolved count from data field', ep, resolved);
              break;
            }
          }

          // If API returned nothing usable, continue to next endpoint
        } catch (err: any) {
          lastErr = err;
          // eslint-disable-next-line no-console
          console.debug('[useShortlistCount] endpoint failed', ep, err?.message ?? err);
          // try next endpoint
        }
      }

      if (!cancelled) {
        if (resolved !== null) {
          setCount(resolved);
          setError(null);
        } else {
          const msg = lastErr?.message ? `All endpoints failed: ${lastErr.message}` : 'No usable data returned from shortlist endpoints';
          setError(new Error(msg));
          setCount(null);
          // eslint-disable-next-line no-console
          console.warn('[useShortlistCount] could not resolve count:', msg);
        }
        setLoading(false);
      }
    }

    load();

    if (pollInterval && pollInterval > 500) {
      intervalId = window.setInterval(() => {
        load();
      }, pollInterval);
    }

    return () => {
      cancelled = true;
      if (typeof intervalId !== 'undefined') {
        window.clearInterval(intervalId);
      }
    };
    // userId and pollInterval are the reactive deps
  }, [userId, pollInterval]);

  return { count, loading, error };
}